import { useState } from "react";
import { ActivityItemShell } from "../../shell/ActivityItemShell";
import { ActivityShellAction } from "../../shell/ActivityShellTypes";
import { ActivityPluginProps } from "../../registry/ActivityPluginTypes";
import { deleteTaskAction } from "../../../ActivityHistory";
import { TranslationService } from "../../../../../../services/TranslationService";
import CompanyService from "../../../../../../services/CompanyService";
import TaskService from "../../../../../../services/TaskSerivce";
import FileService from "../../../../../../services/FileService";
import FloatingPanelService from "../../../../../shared/FloatingPanel";
import TooltipComponent from "../../../../../shared/TooltipComponent";
import TaskDetail from "../../../../../task/components/TaskDetail";
import { AIAgentStrip } from "../../../components/ActivityEmailDisplay";
import { LabelActionText } from "../../../components/NewComment";
import {
  ActivityOrigin,
  ActivityListResponse
} from "../../../entities/AcitivityListResponse";
import { TaskStatus } from "../../../../../task/entities/TaskStatus";
import { Switch, isNullOrWhitespace } from "../../../../../../utils/Utils";
import {
  formatDateDigits,
  formatDateTimeDigits
} from "../../../../../../utils/FormatUtils";
import { restTimezone } from "../../../../../../utils/ParseUtils";
import iconTask from "../../../../../../assets/img/taskIcon.svg";
import iconTaskPending from "../../../../../../assets/img/icon-task-pending.svg";
import { SanitizeUtils } from "../../../../../../utils/SanitizeUtils";
import { SafeHtml } from "../../../../../../components/SafeHtml";

const DisplayActivityType = (data: object | { typeName?: string }) => {
  if ("typeName" in data && data.typeName !== undefined) {
    return <>{TranslationService.getTranslation(data.typeName)}</>;
  }
  return <></>;
};

const DisplayActivityName = (
  data: { ActivityType?: { ActivityTypeName: string } } | object
) => {
  if (
    "ActivityType" in data &&
    data.ActivityType?.ActivityTypeName !== undefined
  ) {
    return <> - {data.ActivityType.ActivityTypeName}</>;
  }
  return <></>;
};

const DisplayGroupName = (
  data: { groupName?: string; Group?: { Name: string } } | object
) => {
  if (
    "groupName" in data &&
    data.groupName &&
    (data.groupName as string).length > 0
  ) {
    return (
      <div style={{ marginRight: 10 }}>
        <strong>{CompanyService.getGroupName()}</strong>:{" "}
        <em>{data.groupName as string}</em>
      </div>
    );
  }
  if ("Group" in data && (data as { Group?: { Name: string } }).Group?.Name) {
    return (
      <div style={{ marginRight: 10 }}>
        <strong>{CompanyService.getGroupName()}</strong>:{" "}
        <em>{(data as { Group: { Name: string } }).Group.Name}</em>
      </div>
    );
  }
  return <></>;
};

const DisplayTagIcon = (
  data: { tagList: string } | { tags: string } | object,
  list: boolean
) => {
  let tags = "";
  if ("tagList" in data) tags = data.tagList;
  else if ("tags" in data) tags = data.tags;
  if (!tags || tags.length === 0) return <></>;
  return (
    <div className="d-flex align-items-center">
      <TooltipComponent title={TranslationService.translate.Tags}>
        <i className="fa fa-tag activity-icon-color my-2" />
      </TooltipComponent>
      {list && (
        <div className="d-flex align-items-center gap-2">
          {tags.split(",").map((tag, i) => (
            <div key={i}>
              <LabelActionText bgColor={false} text={tag} action={() => {}} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TaskFileList = ({ data }: { data: ActivityListResponse.Item }) => {
  const [showFiles, setShowFiles] = useState(false);
  const files = (data.files ?? []).filter((f) => !f.inline);
  if (files.length === 0) return null;

  const download = (fileId: string, fileName: string) =>
    FileService.downloadTask(fileId, data.ID.toString(), fileName);

  return (
    <>
      <TooltipComponent title={TranslationService.translate.AttachedFile}>
        <i
          className="far fa-fw fa-paperclip activity-icon-color my-2"
          onClick={(e) => {
            e.stopPropagation();
            setShowFiles(!showFiles);
          }}
        />
      </TooltipComponent>
      {showFiles && (
        <>
          <div
            className="modal-backdrop fade show attachments-modal-backdrop"
            onClick={() => setShowFiles(false)}
          />
          <div
            className="modal fade show attachments-modal"
            tabIndex={-1}
            style={{
              display: "block",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              style={{
                margin: "auto",
                height: "100%",
                display: "flex",
                alignItems: "center"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {TranslationService.translate.Attachments}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowFiles(false)}
                  />
                </div>
                <div className="modal-body">
                  {files.map((x) => (
                    <div className="col" key={x.id}>
                      <button
                        type="button"
                        className="btn btn-link p-0 m-0 ms-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          download(x.id, x.fileName);
                        }}
                      >
                        {x.fileName}
                      </button>
                    </div>
                  ))}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowFiles(false)}
                  >
                    {TranslationService.translate.Close}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export const ActivityTaskCard = ({
  data,
  reload,
  users
}: ActivityPluginProps) => {
  const [loadingTaskCheck, setLoadingTaskCheck] = useState(false);
  const { translate } = TranslationService;

  const displayUsername = isNullOrWhitespace(data.Username)
    ? isNullOrWhitespace(data.CreatorUsername)
      ? data.CollectorName
      : data.CreatorUsername
    : data.Username;

  const username =
    Switch(
      [
        () =>
          data.Status === TaskStatus.Validated &&
          data.ValidatorUserID !== undefined,
        users.find((x) => x.Id === data.ValidatorUserID?.toString())?.Value
      ],
      [
        () =>
          data.Status === TaskStatus.Fulfilled &&
          !isNullOrWhitespace(data.ResponsibleUsername),
        data.ResponsibleUsername
      ],
      [true, displayUsername]
    ) ?? displayUsername;

  const isPending = data.Status === TaskStatus.Pending;

  const onTaskClick =
    CompanyService.canDo("edittask") || CompanyService.canDo("checktask")
      ? async () => {
          setLoadingTaskCheck(true);
          await TaskService.toggle(
            data.ID,
            isPending ? TaskStatus.Fulfilled : TaskStatus.Pending
          );
          await reload(setLoadingTaskCheck);
        }
      : undefined;

  const isAutonomous = data.Origin === ActivityOrigin.Autonomous;

  const openTaskDetail = () =>
    FloatingPanelService.showPanel({
      children: (
        <TaskDetail
          taskId={data.ID}
          clientId={data.PersonId}
          creatorUserId={data.CreatorUserID}
          dateCreated={data.date}
          reload={reload}
          isAutonomous={isAutonomous}
        />
      ),
      title: "",
      height: 800,
      width: 750
    });

  const iconNode = loadingTaskCheck ? (
    <i className="fas fa-spinner-third fa-spin third ms-2" />
  ) : (
    <img
      onClick={onTaskClick}
      className="img-fluid pointer"
      src={isPending ? iconTaskPending : iconTask}
      alt={isPending ? "icon task pending" : "icon task"}
    />
  );

  const headerNode = (
    <>
      <div className="type">
        {DisplayActivityType(data)}
        <span className="creator">
          {translate.To.toLowerCase()}{" "}
          {(username ?? "").split(", ").reverse().join(" ")}
          {DisplayActivityName(data)}
        </span>
      </div>
      <SafeHtml html={data.Content} style={{ whiteSpace: "pre-line" }} />
    </>
  );

  const contentNode = (
    <>
      {DisplayGroupName(data)}
      <div
        className="d-flex align-items-center justify-center gap-1"
        style={{ color: "#777676" }}
      >
        <TaskFileList data={data} />
        {DisplayTagIcon(data, true)}
      </div>
    </>
  );

  const date = new Date(data.date);
  const dateNode = (
    <div className="dateTertiaryQ d-flex align-items-center">
      <span className="fw-bold">
        {date.getHours() === 0 && date.getMinutes() === 0
          ? formatDateDigits(date)
          : formatDateTimeDigits(restTimezone(date))}
      </span>
    </div>
  );

  const actions: ActivityShellAction[] = CompanyService.canDo("edittask")
    ? [
        {
          icon: "fa-pen text-secondary",
          onClick: openTaskDetail,
          tooltip: translate.Edit
        },
        deleteTaskAction(data.ID, () => reload())
      ]
    : [];

  const aiStripNode = isAutonomous ? (
    <AIAgentStrip text={translate.TaskCreatedByAIAgent} />
  ) : undefined;

  const compactLine = (
    <>
      <strong>{username}</strong>
      {" - "}
      {SanitizeUtils.htmlToPlainText(data.Content ?? "")}
    </>
  );

  return (
    <ActivityItemShell
      cardClassName="historyCard"
      iconNode={iconNode}
      headerNode={headerNode}
      dateNode={dateNode}
      content={contentNode}
      actions={actions}
      aiStripNode={aiStripNode}
      compactLine={compactLine}
    />
  );
};
